import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Lock, Download, RefreshCw, Inbox, Plus, Pencil, Trash2, X } from "lucide-react";
import api from "@/lib/api";

const STATUSES = ["new", "reviewed", "contacted"];

const statusCls = {
  new: "bg-brand-light text-brand-red",
  reviewed: "bg-amber-50 text-amber-700",
  contacted: "bg-emerald-50 text-emerald-700",
};

function StatusButton({ status, onCycle, testId }) {
  return (
    <button onClick={onCycle} data-testid={testId}
      className={`font-mono text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full transition-all duration-300 hover:scale-105 ${statusCls[status] || statusCls.new}`}>
      {status || "new"}
    </button>
  );
}

const emptyJob = {
  title: "", client: "", location: "", employment_type: "", experience: "",
  description: "", responsibilities: "", skills: "", active: true,
};

const jobInput =
  "w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-brand-red focus:ring-2 focus:ring-brand-red/15 transition-all";

export default function AdminInbox() {
  const [token, setToken] = useState(() => sessionStorage.getItem("gs_admin") || "");
  const [password, setPassword] = useState("");
  const [tab, setTab] = useState("enquiries");
  const [enquiries, setEnquiries] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [jobForm, setJobForm] = useState(null);
  const [savingJob, setSavingJob] = useState(false);
  const [loading, setLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [e, c, j, r] = await Promise.all([
        api.get("/admin/enquiries", { headers }),
        api.get("/admin/candidates", { headers }),
        api.get("/admin/jobs", { headers }),
        api.get("/admin/requirements", { headers }),
      ]);
      setEnquiries(e.data);
      setCandidates(c.data);
      setJobs(j.data);
      setRequirements(r.data);
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem("gs_admin");
        setToken("");
      } else {
        toast.error("Could not load inbox.");
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/login", { password });
      sessionStorage.setItem("gs_admin", data.token);
      setToken(data.token);
    } catch (err) {
      toast.error("Invalid password.");
    }
  };

  const cycleStatus = async (kind, item) => {
    const next = STATUSES[(STATUSES.indexOf(item.status || "new") + 1) % STATUSES.length];
    try {
      await api.patch(`/admin/${kind}/${item.id}`, { status: next }, { headers });
      const setter = kind === "enquiries" ? setEnquiries : setCandidates;
      setter((prev) => prev.map((x) => (x.id === item.id ? { ...x, status: next } : x)));
    } catch {
      toast.error("Could not update status.");
    }
  };

  const downloadResume = async (c) => {
    try {
      const res = await api.get(`/admin/candidates/${c.id}/resume`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = c.resume_original_name || "resume";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Resume not available.");
    }
  };

  const saveJob = async (e) => {
    e.preventDefault();
    if (!jobForm.title.trim()) {
      toast.error("Job title is required.");
      return;
    }
    setSavingJob(true);
    const payload = {
      title: jobForm.title,
      client: jobForm.client,
      location: jobForm.location,
      employment_type: jobForm.employment_type,
      experience: jobForm.experience,
      description: jobForm.description,
      responsibilities: jobForm.responsibilities.split("\n").map((s) => s.trim()).filter(Boolean),
      skills: jobForm.skills.split(",").map((s) => s.trim()).filter(Boolean),
      active: jobForm.active,
    };
    try {
      if (jobForm.id) {
        await api.patch(`/admin/jobs/${jobForm.id}`, payload, { headers });
        toast.success("Job updated.");
      } else {
        await api.post("/admin/jobs", payload, { headers });
        toast.success("Job published.");
      }
      setJobForm(null);
      load();
    } catch {
      toast.error("Could not save job.");
    } finally {
      setSavingJob(false);
    }
  };

  const deleteJob = async (job) => {
    if (!window.confirm(`Delete "${job.title}" permanently?`)) return;
    try {
      await api.delete(`/admin/jobs/${job.id}`, { headers });
      toast.success("Job removed.");
      load();
    } catch {
      toast.error("Could not delete job.");
    }
  };

  const toggleJobActive = async (job) => {
    try {
      await api.patch(`/admin/jobs/${job.id}`, {
        title: job.title, client: job.client, location: job.location,
        employment_type: job.employment_type, experience: job.experience,
        description: job.description, responsibilities: job.responsibilities || [],
        skills: job.skills || [], active: !job.active,
      }, { headers });
      load();
    } catch {
      toast.error("Could not update job.");
    }
  };

  if (!token) {
    return (
      <section className="pt-[140px] pb-20 min-h-[70vh]" data-testid="admin-login-section">
        <div className="max-w-md mx-auto px-4">
          <form onSubmit={login} className="bg-white border border-slate-200 rounded-3xl p-10 shadow-[0_30px_70px_-40px_rgba(14,15,19,0.3)]">
            <span className="inline-flex p-3 rounded-2xl bg-brand-light text-brand-red"><Lock size={20} /></span>
            <h1 className="mt-6 font-display font-bold text-slate-900 text-2xl">Admin Inbox</h1>
            <p className="mt-2 text-sm text-slate-500">Enter the admin password to view enquiries, candidates and manage job openings.</p>
            <input type="password" required placeholder="Admin password" value={password} onChange={(e) => setPassword(e.target.value)}
              className="mt-6 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-brand-red focus:ring-2 focus:ring-brand-red/15 transition-all"
              data-testid="admin-password-input" />
            <button type="submit" data-testid="admin-login-button"
              className="mt-4 w-full bg-brand-red hover:bg-brand-dark text-white font-medium text-sm px-6 py-3.5 rounded-xl transition-colors duration-300">
              Sign In
            </button>
          </form>
        </div>
      </section>
    );
  }

  const tabLabels = {
    requirements: `Requirements (${requirements.length})`,
    enquiries: `Enquiries (${enquiries.length})`,
    candidates: `Candidates (${candidates.length})`,
    jobs: `Job Openings (${jobs.length})`,
  };

  return (
    <section className="pt-[124px] pb-20 min-h-[70vh]" data-testid="admin-inbox-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display font-bold text-slate-900 text-3xl flex items-center gap-3">
              <Inbox size={26} className="text-brand-red" /> Admin Inbox
            </h1>
            <p className="mt-2 text-sm text-slate-500">Employer enquiries, candidate submissions and job openings.</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} data-testid="admin-refresh-button"
              className="inline-flex items-center gap-2 border border-slate-300 hover:border-brand-red text-slate-700 hover:text-brand-red text-sm font-medium px-5 py-2.5 rounded-full transition-colors">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={() => { sessionStorage.removeItem("gs_admin"); setToken(""); }} data-testid="admin-logout-button"
              className="text-sm text-slate-400 hover:text-brand-red transition-colors">
              Sign out
            </button>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats">
          {[
            { label: "Employer Requirements", value: requirements.length, sub: `${requirements.filter((r) => (r.status || "new") === "new").length} new`, testid: "admin-stat-requirements" },
            { label: "Candidate Profiles", value: candidates.length, sub: `${candidates.filter((c) => (c.status || "new") === "new").length} new`, testid: "admin-stat-candidates" },
            { label: "General Enquiries", value: enquiries.length, sub: `${enquiries.filter((e) => (e.status || "new") === "new").length} new`, testid: "admin-stat-enquiries" },
            { label: "Live Job Openings", value: jobs.filter((j) => j.active).length, sub: `${jobs.length} total`, testid: "admin-stat-jobs" },
          ].map((s) => (
            <div key={s.testid} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={s.testid}>
              <p className="font-display font-extrabold text-slate-900 text-3xl">{s.value}</p>
              <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{s.label}</p>
              <p className="mt-1 text-xs text-brand-red font-medium">{s.sub}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-tabs">
          {["requirements", "enquiries", "candidates", "jobs"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-colors duration-300 ${tab === t ? "bg-brand-red text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {tabLabels[t]}
            </button>
          ))}
        </div>

        {tab === "requirements" && (
          <div className="mt-6 space-y-4" data-testid="admin-requirements-list">
            {requirements.length === 0 && <p className="text-sm text-slate-400 py-10 text-center">No requirements yet.</p>}
            {requirements.map((r) => (
              <div key={r.id} className="bg-white border border-slate-200 rounded-2xl p-6 grid md:grid-cols-12 gap-4" data-testid={`admin-requirement-${r.id}`}>
                <div className="md:col-span-3">
                  <p className="font-semibold text-slate-900 text-sm">{r.name}</p>
                  <p className="text-xs text-slate-500 mt-1">{r.company}</p>
                  <p className="text-xs text-slate-500">{r.work_email}</p>
                  <p className="text-xs text-slate-500">{r.phone}{r.designation ? ` · ${r.designation}` : ""}</p>
                </div>
                <div className="md:col-span-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Requirement</p>
                  <p className="text-sm text-slate-800 mt-1">{(r.requirements || []).join(", ")}</p>
                  <p className="text-xs text-slate-500 mt-1.5">
                    {[r.role, (r.positions || r.headcount) && `${r.positions || r.headcount} position(s)`, r.skills, r.locations].filter(Boolean).join(" · ")}
                  </p>
                  <p className="text-xs text-brand-red mt-1.5 font-medium">{r.timeline}</p>
                </div>
                <div className="md:col-span-3">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Details</p>
                  <p className="text-sm text-slate-600 mt-1 leading-relaxed">{r.details || "—"}</p>
                  {r.timeline_notes && <p className="text-xs text-slate-500 mt-2">{r.timeline_notes}</p>}
                </div>
                <div className="md:col-span-2 flex md:flex-col items-start justify-between gap-2">
                  <StatusButton status={r.status} testId={`admin-requirement-status-${r.id}`} onCycle={() => cycleStatus("requirements", r)} />
                  <p className="text-[10px] text-slate-400">{new Date(r.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "enquiries" && (
          <div className="mt-6 space-y-4" data-testid="admin-enquiries-list">
            {enquiries.length === 0 && <p className="text-sm text-slate-400 py-10 text-center">No enquiries yet.</p>}
            {enquiries.map((e) => (
              <div key={e.id} className="bg-white border border-slate-200 rounded-2xl p-6 grid md:grid-cols-12 gap-4" data-testid={`admin-enquiry-${e.id}`}>
                <div className="md:col-span-3">
                  <p className="font-semibold text-slate-900 text-sm">{e.name}</p>
                  <p className="text-xs text-slate-500 mt-1">{e.company || "—"}</p>
                  <p className="text-xs text-slate-500">{e.work_email}</p>
                  <p className="text-xs text-slate-500">{e.phone || ""}</p>
                </div>
                <div className="md:col-span-3">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Requirement</p>
                  <p className="text-sm text-slate-800 mt-1">{e.hiring_requirement}</p>
                  {e.positions && <p className="text-xs text-slate-500 mt-1">{e.positions} position(s)</p>}
                </div>
                <div className="md:col-span-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Message</p>
                  <p className="text-sm text-slate-600 mt-1 leading-relaxed">{e.message || "—"}</p>
                </div>
                <div className="md:col-span-2 flex md:flex-col items-start justify-between gap-2">
                  <StatusButton status={e.status} testId={`admin-enquiry-status-${e.id}`} onCycle={() => cycleStatus("enquiries", e)} />
                  <p className="text-[10px] text-slate-400">{new Date(e.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "candidates" && (
          <div className="mt-6 space-y-4" data-testid="admin-candidates-list">
            {candidates.length === 0 && <p className="text-sm text-slate-400 py-10 text-center">No candidate submissions yet.</p>}
            {candidates.map((c) => (
              <div key={c.id} className="bg-white border border-slate-200 rounded-2xl p-6 grid md:grid-cols-12 gap-4" data-testid={`admin-candidate-${c.id}`}>
                <div className="md:col-span-3">
                  <p className="font-semibold text-slate-900 text-sm">{c.name}</p>
                  <p className="text-xs text-slate-500 mt-1">{c.email}</p>
                  <p className="text-xs text-slate-500">{c.phone || ""}</p>
                  {(c.current_location || c.preferred_location) && (
                    <p className="text-xs text-slate-500 mt-1">{c.current_location}{c.preferred_location ? ` → ${c.preferred_location}` : ""}</p>
                  )}
                </div>
                <div className="md:col-span-3">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Profile</p>
                  <p className="text-sm text-slate-800 mt-1">{c.designation || c.role_interest || "—"}</p>
                  {c.total_experience && <p className="text-xs text-slate-500 mt-1">{c.total_experience} experience</p>}
                  {c.skills && <p className="text-xs text-slate-500 mt-1">{c.skills}</p>}
                  {c.applied_for && <p className="text-xs text-brand-red mt-1 font-medium">Applied: {c.applied_for}</p>}
                </div>
                <div className="md:col-span-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Message</p>
                  <p className="text-sm text-slate-600 mt-1 leading-relaxed">{c.message || "—"}</p>
                  {c.resume_original_name && (
                    <button onClick={() => downloadResume(c)} data-testid={`admin-resume-download-${c.id}`}
                      className="mt-3 flex items-center gap-2 text-xs font-medium text-brand-red hover:text-brand-dark transition-colors">
                      <Download size={13} /> {c.resume_original_name}
                    </button>
                  )}
                </div>
                <div className="md:col-span-2 flex md:flex-col items-start justify-between gap-2">
                  <StatusButton status={c.status} testId={`admin-candidate-status-${c.id}`} onCycle={() => cycleStatus("candidates", c)} />
                  <p className="text-[10px] text-slate-400">{new Date(c.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "jobs" && (
          <div className="mt-6" data-testid="admin-jobs-section">
            {!jobForm && (
              <button onClick={() => setJobForm({ ...emptyJob })} data-testid="admin-add-job-button"
                className="inline-flex items-center gap-2 bg-brand-red hover:bg-brand-dark text-white text-sm font-medium px-6 py-3 rounded-full transition-colors">
                <Plus size={15} /> New Job Opening
              </button>
            )}

            {jobForm && (
              <form onSubmit={saveJob} className="mt-2 bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 grid sm:grid-cols-2 gap-4" data-testid="admin-job-form">
                <div className="sm:col-span-2 flex items-center justify-between">
                  <h3 className="font-display font-semibold text-slate-900 text-lg">{jobForm.id ? "Edit Job" : "New Job Opening"}</h3>
                  <button type="button" onClick={() => setJobForm(null)} className="text-slate-400 hover:text-brand-red" data-testid="admin-job-cancel"><X size={18} /></button>
                </div>
                <input required placeholder="Job Title *" value={jobForm.title} onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })} className={jobInput} data-testid="job-title-input" />
                <input placeholder="Client / Company" value={jobForm.client} onChange={(e) => setJobForm({ ...jobForm, client: e.target.value })} className={jobInput} data-testid="job-client-input" />
                <input placeholder="Location" value={jobForm.location} onChange={(e) => setJobForm({ ...jobForm, location: e.target.value })} className={jobInput} data-testid="job-location-input" />
                <input placeholder="Employment Type (e.g. Full-time, Contract)" value={jobForm.employment_type} onChange={(e) => setJobForm({ ...jobForm, employment_type: e.target.value })} className={jobInput} data-testid="job-type-input" />
                <input placeholder="Experience Required (e.g. 3–5 years)" value={jobForm.experience} onChange={(e) => setJobForm({ ...jobForm, experience: e.target.value })} className={jobInput} data-testid="job-experience-input" />
                <label className="flex items-center gap-3 text-sm text-slate-600 px-1">
                  <input type="checkbox" checked={jobForm.active} onChange={(e) => setJobForm({ ...jobForm, active: e.target.checked })} className="accent-brand-red h-4 w-4" data-testid="job-active-toggle" />
                  Published (visible on Current Openings)
                </label>
                <textarea placeholder="Job Description" rows={4} value={jobForm.description} onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })} className={`${jobInput} sm:col-span-2 resize-none`} data-testid="job-description-input" />
                <textarea placeholder="Key Responsibilities (one per line)" rows={4} value={jobForm.responsibilities} onChange={(e) => setJobForm({ ...jobForm, responsibilities: e.target.value })} className={`${jobInput} resize-none`} data-testid="job-responsibilities-input" />
                <textarea placeholder="Required Skills / Qualifications (comma separated)" rows={4} value={jobForm.skills} onChange={(e) => setJobForm({ ...jobForm, skills: e.target.value })} className={`${jobInput} resize-none`} data-testid="job-skills-input" />
                <button type="submit" disabled={savingJob} data-testid="admin-job-save"
                  className="sm:col-span-2 bg-brand-red hover:bg-brand-dark text-white text-sm font-medium px-6 py-3.5 rounded-xl transition-colors disabled:opacity-60">
                  {savingJob ? "Saving…" : jobForm.id ? "Update Job" : "Publish Job"}
                </button>
              </form>
            )}

            <div className="mt-6 space-y-4" data-testid="admin-jobs-list">
              {jobs.length === 0 && !jobForm && <p className="text-sm text-slate-400 py-10 text-center">No job openings yet. Publish your first role above.</p>}
              {jobs.map((j) => (
                <div key={j.id} className="bg-white border border-slate-200 rounded-2xl p-6 flex flex-wrap items-start justify-between gap-4" data-testid={`admin-job-${j.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-3">
                      <p className="font-semibold text-slate-900 text-sm">{j.title}</p>
                      <span className={`font-mono text-[9px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full ${j.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                        {j.active ? "Published" : "Hidden"}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1.5">
                      {[j.client, j.location, j.employment_type, j.experience].filter(Boolean).join(" · ") || "—"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => toggleJobActive(j)} data-testid={`admin-job-toggle-${j.id}`}
                      className="text-xs font-medium border border-slate-300 hover:border-brand-red text-slate-600 hover:text-brand-red px-4 py-2 rounded-full transition-colors">
                      {j.active ? "Unpublish" : "Publish"}
                    </button>
                    <button onClick={() => setJobForm({ ...j, responsibilities: (j.responsibilities || []).join("\n"), skills: (j.skills || []).join(", ") })}
                      data-testid={`admin-job-edit-${j.id}`}
                      className="inline-flex items-center gap-1.5 text-xs font-medium border border-slate-300 hover:border-brand-red text-slate-600 hover:text-brand-red px-4 py-2 rounded-full transition-colors">
                      <Pencil size={12} /> Edit
                    </button>
                    <button onClick={() => deleteJob(j)} data-testid={`admin-job-delete-${j.id}`}
                      className="inline-flex items-center gap-1.5 text-xs font-medium border border-slate-300 hover:border-brand-red text-slate-600 hover:text-brand-red px-4 py-2 rounded-full transition-colors">
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
